import { CheckCircle, XCircle } from 'lucide-react'
import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import type { TestResult, TestSummary } from '../execution-context'
import { CaseDetail } from './case-detail'

interface RunResultsViewProps {
  results: TestResult[]
  summary: TestSummary
}

/**
 * Run mode: one tab per example case (Case 1 · Case 2 · Case 3) with a
 * pass/fail dot. The first failing case is preselected. Mount with a key
 * tied to the execution (executionId) so tab selection resets on each run.
 */
export function RunResultsView({ results, summary }: RunResultsViewProps) {
  const defaultCase = results.find((r) => !r.passed) ?? results[0]
  const [selected, setSelected] = useState<string | undefined>(
    defaultCase ? `case-${defaultCase.index}` : undefined,
  )
  const allPassed = summary.failed === 0

  if (results.length === 0) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center p-4 text-sm">
        No test cases were executed.
      </div>
    )
  }

  return (
    <div className="h-full space-y-4 overflow-auto p-4">
      {/* Summary header */}
      <div className="flex items-center gap-3">
        {allPassed ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500" />
        )}
        <h2
          className={cn(
            'text-lg font-semibold',
            allPassed ? 'text-green-500' : 'text-red-500',
          )}
        >
          {allPassed ? 'All example cases passed' : 'Example case failed'}
        </h2>
        <span className="text-muted-foreground text-sm">
          {summary.passed}/{summary.total} passed
        </span>
      </div>

      {/* Per-case tabs */}
      <Tabs value={selected} onValueChange={setSelected} className="gap-3">
        <TabsList className="h-auto flex-wrap">
          {results.map((r) => (
            <TabsTrigger key={`trigger-${r.index}`} value={`case-${r.index}`}>
              <span
                className={cn(
                  'inline-block h-2 w-2 rounded-full',
                  r.passed ? 'bg-green-500' : 'bg-red-500',
                )}
                aria-hidden="true"
              />
              Case {r.index + 1}
              <span className="sr-only">
                {r.passed ? ' (passed)' : ' (failed)'}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
        {results.map((r) => (
          <TabsContent key={`content-${r.index}`} value={`case-${r.index}`}>
            {/* Render only the active tab so a hidden tab's FailurePanel
                can't claim the Monaco error marker. */}
            {selected === `case-${r.index}` && <CaseDetail result={r} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
